import { compareInventoryRows, formatInventoryNumber, isInventorySoldOut, matchesInventorySearch } from "../utils/inventoryDisplay";
import { englishError } from "../utils/databaseErrors";
// src/components/WelcomeTab.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { availableQuantity, PRODUCT_TYPES, reservedQuantity } from "../utils/stock";
import { sumQuantities } from "../utils/marbleQuantity";

/* ===== Helpers للعرض ===== */
const treatMap = { epoxy: "Epoxy", resin: "Resin", none: "None" };

function formatVariant(d) {
  const face = d?.face_treatment || "none";
  const back = d?.back_treatment || "none";
  const pol = !!d?.is_polished;
  return `Front: ${treatMap[face]} | Back: ${treatMap[back]} | ${pol ? "Polished" : "Unpolished"}`;
}

function codeWithSuffix(blockCode, dist) {
  const sfx = (dist?.variant_suffix || "").trim();
  if (sfx) return `${blockCode}-${sfx}`;

  // اختصار تلقائي لو مفيش suffix صريح
  const f = dist?.face_treatment || "none";
  const b = dist?.back_treatment || "none";
  const p = dist?.is_polished ? "P" : "";
  const short =
    (f === "epoxy" ? "FE" : f === "resin" ? "FR" : "") +
    (b === "epoxy" ? "BE" : b === "resin" ? "BR" : "") +
    p;
  return short ? `${blockCode}-${short}` : blockCode;
}

function WelcomeTab({ user }) {
  const [materials, setMaterials] = useState([]);
  const [blockDistributions, setBlockDistributions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [error, setError] = useState("");

  // فلاتر (Drop-down)
  const [selMaterial, setSelMaterial] = useState("all");
  const [selSupplier, setSelSupplier] = useState("all");
  const [search, setSearch] = useState("");

  // فتح/غلق تفاصيل التوزيع لكل منتج
  const [expanded, setExpanded] = useState({}); // { [blockId]: true/false }

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  async function fetchAll() {
    setError("");
    const { data: bookings, error: reservationError } = await supabase.from("reservation").select("*");
    if (reservationError) { setError("Unable to load reserved quantities: " + englishError(reservationError.message)); return; }
    setReservations(bookings || []);
    const { data: mats } = await supabase.from("material").select("*");
    setMaterials(mats || []);

    const { data: bd } = await supabase.from("block_distribution").select("*");
    setBlockDistributions(bd || []);

    const allBlockIds = [...new Set((bd || []).map((d) => d.block_id))];
    if (allBlockIds.length > 0) {
      const { data: bls } = await supabase
        .from("block")
        .select("*")
        .in("id", allBlockIds);
      setBlocks(bls || []);
    } else {
      setBlocks([]);
    }

    const { data: custs } = await supabase.from("customer").select("*");
    setCustomers(custs || []);

    const { data: shrs } = await supabase.from("showroom").select("*");
    setShowrooms(shrs || []);
  }

  const materialLookup = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m.material_name || m.name])),
    [materials]
  );
  const showroomLookup = useMemo(
    () => Object.fromEntries(showrooms.map((s) => [s.id, s.name])),
    [showrooms]
  );
  const supplierLookup = useMemo(
    () => Object.fromEntries(customers.map((customer) => [String(customer.id), customer.name])),
    [customers]
  );

  function getShowroomName(showroom_id) {
    return showroomLookup[showroom_id] || `Showroom ${showroom_id}`;
  }
  function toggleExpand(blockId) {
    setExpanded((e) => ({ ...e, [blockId]: !e[blockId] }));
  }

  // صفوف الجدول: كل توزيع للمنتج داخل معرض المستخدم فقط
  const stockRows = useMemo(() => {
    const distsInThisShowroom = blockDistributions.filter(
      (d) => String(d.showroom_id) === String(user.showroom_id)
    );

    const list = distsInThisShowroom
      .map((d) => {
        const block = blocks.find((b) => String(b.id) === String(d.block_id));
        if (!block) return null;

        const approxArea =
          block.total_area && block.slabs_number
            ? (
                (Number(block.total_area) * Number(d.slabs_count)) /
                Number(block.slabs_number)
              ).toFixed(2)
            : "";

        return {
          blockId: block.id,
          code: block.code,
          codeLabel: codeWithSuffix(block.code, d),
          materialId: block.material_id,
          materialName: materialLookup[block.material_id] || block.material_id,
          productType: PRODUCT_TYPES[block.product_type || "slab"],
          supplierId: block.customer_id,
          supplierName: supplierLookup[String(block.customer_id)] || "-",
          slabs: Number(d.slabs_count),
          reserved: reservedQuantity(reservations, d.id),
          available: availableQuantity(d, reservations),
          area: approxArea,
          block,
          dist: d,
        };
      })
      .filter(Boolean);

    return list.sort(compareInventoryRows);
  }, [
    blockDistributions,
    reservations,
    blocks,
    materialLookup,
    supplierLookup,
    user.showroom_id,
  ]);

  const rows = useMemo(() => stockRows.filter(row =>
    (selMaterial === "all" || String(row.materialId) === String(selMaterial)) &&
    (selSupplier === "all" || String(row.supplierId) === String(selSupplier)) &&
    matchesInventorySearch(row, search)
  ), [stockRows, selMaterial, selSupplier, search]);

  // خيارات الفلاتر من البيانات المعروضة
  const materialOptions = useMemo(() => {
    const setIds = new Set(stockRows.map((r) => r.materialId));
    return Array.from(setIds).map((id) => ({ id, name: materialLookup[id] || id }));
  }, [stockRows, materialLookup]);

  const supplierOptions = useMemo(() => {
    const map = new Map();
    stockRows.forEach((r) => map.set(r.supplierId, r.supplierName));
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [stockRows]);

  // ستايل مضغوط
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    background: "#faf8f4",
    borderRadius: 10,
    direction: "ltr",
    textAlign: "left",
    fontSize: 12,
  };
  const thtd = {
    borderBottom: "1px solid #d2cbc1",
    padding: "4px 6px",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  };

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 1100, margin: "auto", padding: 18 }}>
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 8 }}>
        Welcome, {user.name} 👋
      </h2>
      <div style={{ color: "#716759", marginBottom: 10, fontSize: 13 }}>
        Stock in <b>{getShowroomName(user.showroom_id)}</b>
      </div>
      {error && <p role="alert" style={{ color: "#514536" }}>{error}</p>}
      <p>Physical quantity: {sumQuantities(rows.map(row => row.slabs))} · Reserved: {sumQuantities(rows.map(row => row.reserved))} · Available to sell: {sumQuantities(rows.map(row => row.available))} pieces</p>
      <button className="ui-secondary" type="button" onClick={fetchAll}>Refresh inventory</button>

      {/* فلاتر Drop-down + إعادة ضبط */}
      <div className="ui-toolbar"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <label className="ui-field">Material type:<select
          value={selMaterial}
          onChange={(e) => setSelMaterial(e.target.value)}
          style={{
            minWidth: 180,
            padding: "6px 8px",
            borderRadius: 8,
            border: "1px solid #d2cbc1",
            background: "#faf8f4",
            fontSize: 12,
          }}
        >
          <option value="all">All</option>
          {materialOptions.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select></label>

        <label className="ui-field">Supplier:<select
          value={selSupplier}
          onChange={(e) => setSelSupplier(e.target.value)}
          style={{
            minWidth: 180,
            padding: "6px 8px",
            borderRadius: 8,
            border: "1px solid #d2cbc1",
            background: "#faf8f4",
            fontSize: 12,
          }}
        >
          <option value="all">All</option>
          {supplierOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select></label>

        <label className="ui-field">Search material or block code
          <input type="search" value={search} onChange={event => setSearch(event.target.value)} />
        </label>

        <div style={{ marginRight: "auto" }} />

        <button className="ui-secondary"
          onClick={() => {
            setSelMaterial("all");
            setSelSupplier("all");
            setSearch("");
            setExpanded({});
          }}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "1px solid #d2cbc1",
            background: "#d2cbc1",
            fontWeight: "bold",
            color: "var(--accent, #514536)",
            cursor: "pointer",
            fontSize: 12,
          }}
        >
          Reset
        </button>
      </div>

      <div className="ui-table-scroll"><table style={tableStyle}>
        <thead>
          <tr style={{ background: "#f2ede4" }}>
            <th style={thtd}>No.</th>
            <th style={thtd}>Product code</th>
            <th style={thtd}>Material</th>
            <th style={thtd}>Product type</th>
            <th style={thtd}>Supplier</th>
            <th style={thtd}>Quantity</th>
            <th style={thtd}>Reserved</th>
            <th style={thtd}>Available to sell</th>
            <th style={thtd}>Area (m²)</th>
            <th style={thtd}>Treatment</th>
            <th style={thtd}>Notes</th>
            <th style={thtd}>Distribution</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const otherDists = blockDistributions.filter(
              (d2) =>
                String(d2.block_id) === String(r.blockId) &&
                String(d2.showroom_id) !== String(user.showroom_id) &&
                Number(d2.slabs_count) > 0
            );
            return (
              <React.Fragment key={`${r.blockId}-${r.dist.id}`}>
                <tr>
                  <td style={thtd}>{idx + 1}</td>
                  <td style={thtd}>{r.codeLabel}</td>
                  <td style={thtd}>{r.materialName}</td>
                  <td style={thtd}>{r.productType}</td>
                  <td style={thtd}>{r.supplierName}</td>
                  <td style={thtd}>{formatInventoryNumber(r.slabs)}</td>
                  <td style={thtd}>{formatInventoryNumber(r.reserved)}</td>
                  <td style={thtd}>{formatInventoryNumber(r.available)}</td>
                  <td style={thtd}>{isInventorySoldOut(r) ? "Sold out" : formatInventoryNumber(r.area)}</td>
                  <td style={thtd}>{formatVariant(r.dist)}</td>
                  <td className="ui-table-notes" style={thtd}>{r.block.notes_text || "-"}</td>
                  <td style={thtd}><div className="ui-actions">
                    <button className="ui-secondary"
                      onClick={() => toggleExpand(r.blockId)}
                      style={{
                        padding: "3px 8px",
                        borderRadius: 8,
                        border: "1px solid #d2cbc1",
                        background: expanded[r.blockId] ? "#f2ede4" : "#faf8f4",
                        fontSize: 11,
                        cursor: "pointer",
                        color: "var(--accent, #514536)",
                      }}
                    >
                      {expanded[r.blockId] ? "Hide" : "Width"}
                    </button>
                  </div></td>
                </tr>

                {expanded[r.blockId] && (
                  <tr>
                    <td colSpan={12} style={{ padding: "6px 6px", background: "#faf8f4" }}>
                      {otherDists.length === 0 ? (
                        <div style={{ color: "#d2cbc1", fontSize: 11 }}>
                          No distributions in other showrooms
                        </div>
                      ) : (
                        <ul
                          style={{
                            margin: 0,
                            marginRight: 14,
                            paddingRight: 0,
                            color: "var(--accent, #514536)",
                            fontSize: 12,
                            lineHeight: 1.6,
                          }}
                        >
                          {otherDists.map((d2) => {
                            const blk = blocks.find((b) => String(b.id) === String(d2.block_id));
                            const approxArea =
                              blk?.total_area && blk?.slabs_number
                                ? (
                                    (Number(blk.total_area) * Number(d2.slabs_count)) /
                                    Number(blk.slabs_number)
                                  ).toFixed(2)
                                : "";
                            return (
                              <li key={d2.id}>
                                {getShowroomName(d2.showroom_id)} — Quantity: {formatInventoryNumber(d2.slabs_count)}
                                {approxArea ? ` / approximately ${approxArea} m²` : ""} —{" "}
                                <b>{formatVariant(d2)}</b> —{" "}
                                <span style={{ color: "#716759" }}>
                                  {codeWithSuffix(blk?.code || d2.block_id, d2)}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

          {rows.length === 0 && (
            <tr>
              <td colSpan={12} style={{ padding: 12, textAlign: "center", color: "#d2cbc1" }}>
                No records match the current filters.
              </td>
            </tr>
          )}
        </tbody>
      </table></div>
    </div>
  );
}

export default WelcomeTab;
